import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Upload, Wand2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useApiBase } from "../context/ApiBaseContext";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import ScriptRevisionPanel from "./ScriptRevisionPanel";
import ConfirmReplaceScriptDialog from "./ConfirmReplaceScriptDialog";
import ScriptIndexingRecovery from "./ScriptIndexingRecovery";
import ScriptParsedBadge from "./ScriptParsedBadge";
import ConfirmRebuildSceneCastDialog from "./ConfirmRebuildSceneCastDialog";

export default function ScriptSection({ projectId }) {
    const apiBase = useApiBase();
    const navigate = useNavigate();
    const fileRef = useRef(null);

    const [script, setScript] = useState(null);
    const [scriptText, setScriptText] = useState("");
    const [scriptTitle, setScriptTitle] = useState("");
    const [uploading, setUploading] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [savedMsg, setSavedMsg] = useState("");
    const [error, setError] = useState("");
    // Iter-157P.14 — structured warning state for base-script
    // zero-scene failures. The backend returns a structured 422
    // payload with active_format + filename + producer-friendly
    // message; the FE renders this in a calm red block matching
    // the revision extraction warning pattern. Set alongside —
    // and distinct from — the generic `error` string so we can
    // surface either depending on what the backend said.
    const [extractionWarning, setExtractionWarning] = useState(null);
    // Iter-61: replacement confirmation. We hold the picked File while
    // the user confirms, then commit on confirm.
    const [pendingFile, setPendingFile] = useState(null);
    // Iter-155B.3 — when the user clicks "Replace script" on a slot
    // that already has a file, surface the confirmation BEFORE
    // opening the OS file picker. (Previously the picker opened
    // first, which felt accidental — users want a deliberate
    // "yes I really want to swap the script" gate before the file
    // chooser appears.)
    const [showReplaceIntent, setShowReplaceIntent] = useState(false);
    // Iter-99 — progress label shown to the user during the
    // upload → indexing → ready handshake. Keeps the UI explicit
    // ("Uploading script…" / "Indexing script…") rather than letting
    // the user stare at a disabled Parse button.
    const [progress, setProgress] = useState("");
    // Iter-141 — recovery affordance state + local fallback for
    // indexing-start when the server didn't stamp it (legacy docs).
    const [recoveryBusy, setRecoveryBusy] = useState(null);
    const [localIndexingStart, setLocalIndexingStart] = useState(null);
    // Iter-147 — confirmation gate before re-running Build Scenes & Cast.
    const [showRebuildConfirm, setShowRebuildConfirm] = useState(false);

    useEffect(() => {
        const ctrl = new AbortController();
        (async () => {
            try {
                const { data: sp } = await api.get(`${apiBase}/${projectId}/script`, {
                    signal: ctrl.signal,
                });
                setScript(sp);
                setScriptTitle(sp?.title || "");
                if (sp?.source === "paste") setScriptText(sp?.content || "");
            } catch (e) {
                if (ctrl.signal.aborted) return;
                setError(formatApiError(e));
            }
        })();
        return () => ctrl.abort();
    }, [projectId]);

    // Iter-99 — once an upload returns `index_status: "indexing"`,
    // poll the script doc until indexing settles. Avoids a 504-prone
    // synchronous extraction inside parse and gives the user clear
    // visual progress.
    useEffect(() => {
        if (!script || script.index_status !== "indexing") return;
        let aborted = false;
        let timer;
        const tick = async () => {
            if (aborted) return;
            try {
                const { data: sp } = await api.get(`${apiBase}/${projectId}/script`);
                if (aborted) return;
                setScript(sp);
                if (sp?.index_status === "indexing") {
                    timer = setTimeout(tick, 1500);
                } else if (sp?.index_status === "ready") {
                    setProgress("");
                    toast.success("Script ready · you can Build Scenes & Cast");
                } else if (sp?.index_status === "failed") {
                    setProgress("");
                    setError(
                        sp.index_error
                            ? `We couldn't finish reading the script: ${sp.index_error}. The file is still saved — try uploading it again.`
                            : "We couldn't finish reading the script. The file is still saved — try uploading it again.",
                    );
                } else if (sp?.index_status === "empty") {
                    setProgress("");
                    setError(
                        "Could not extract any text from the uploaded file. Try a different export, or paste the script.",
                    );
                }
            } catch {
                if (!aborted) timer = setTimeout(tick, 2500);
            }
        };
        timer = setTimeout(tick, 1200);
        return () => {
            aborted = true;
            if (timer) clearTimeout(timer);
        };
    }, [script, projectId]);

    // Iter-141 — Track a local indexing-start fallback when the
    // server didn't stamp `indexing_started_at`. Clears the timer
    // whenever the doc leaves the indexing state.
    useEffect(() => {
        if (script?.index_status === "indexing") {
            if (!script.indexing_started_at && !localIndexingStart) {
                setLocalIndexingStart(new Date().toISOString());
            }
        } else if (localIndexingStart) {
            setLocalIndexingStart(null);
        }
    }, [script, localIndexingStart]);

    const indexingStartedAt =
        script?.indexing_started_at || localIndexingStart || null;

    const recoveryRefresh = async () => {
        setRecoveryBusy("refresh");
        try {
            // Iter-143 — apply server state directly to local state so
            // the tile reflects backend truth verbatim.
            const { data: sp } = await api.get(`${apiBase}/${projectId}/script`);
            setScript(sp);
        } catch {
            // Silent — recovery refresh failures shouldn't surface a
            // banner. The polling effect will pick up the next tick.
        } finally {
            setRecoveryBusy(null);
        }
    };

    const recoveryRetry = async () => {
        setRecoveryBusy("retry");
        try {
            // Iter-143 — reindex is synchronous (iter-142). Read the
            // final state from the response and update the tile
            // immediately. No more local-only timer reset.
            const { data } = await api.post(
                `${apiBase}/${projectId}/script/reindex`,
            );
            setScript((prev) => ({
                ...(prev || {}),
                index_status: data?.index_status ?? prev?.index_status,
                index_error: data?.index_error ?? null,
                indexing_started_at: data?.indexing_started_at ?? null,
                chars: data?.chars !== undefined ? data.chars : prev?.chars,
            }));
            const finalStatus = data?.index_status;
            if (finalStatus === "ready") {
                toast.success("Script ready");
                setError("");
            } else if (finalStatus === "empty") {
                toast.info("Script saved · no text found in the file");
            } else if (finalStatus === "failed") {
                toast.error(
                    data?.index_error
                        ? `We couldn't finish reading the script: ${data.index_error}`
                        : "We couldn't finish reading the script",
                );
            } else {
                toast.info("Reading the script again…");
            }
        } catch (err) {
            toast.error(formatApiError(err));
            // Refresh from server so the tile reflects truth even on
            // a transient retry error.
            try {
                const { data: sp } = await api.get(
                    `${apiBase}/${projectId}/script`,
                );
                setScript(sp);
            } catch {
                /* swallow */
            }
        } finally {
            setRecoveryBusy(null);
        }
    };

    // Iter-155B.4 — single source of truth for "does this Replace
    // need to be deliberately confirmed?". Covers:
    //   - has_original / filename → an actual file was uploaded.
    //   - parsed_at → scenes & cast were built (covers the
    //     pasted-text path that doesn't have a file but still
    //     represents real work the user could lose).
    const shouldConfirmReplace = (s) =>
        Boolean(s?.has_original || s?.filename || s?.parsed_at);

    const recoveryReplace = () => {
        // Route through the same Replace confirmation gate as the
        // normal Replace button. The user shouldn't get a different
        // (less deliberate) experience just because they hit
        // recovery.
        if (shouldConfirmReplace(script)) {
            setShowReplaceIntent(true);
        } else {
            fileRef.current?.click();
        }
    };

    const uploadScript = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        // Iter-155B.3 — file picker now only opens AFTER the user
        // has confirmed Replace intent (or it's a first upload). So
        // by the time we receive a file here, we can commit
        // immediately — no second confirmation dialog needed.
        await commitUpload(file);
        // Clear input so the same file can be re-picked next time.
        if (fileRef.current) fileRef.current.value = "";
    };

    // Iter-155B.3 — button click handler. Routes to intent dialog
    // first when there's an existing script (file OR built-from-paste),
    // otherwise opens the OS file picker immediately.
    const onUploadButtonClick = () => {
        if (uploading || indexing) return;
        if (shouldConfirmReplace(script)) {
            setShowReplaceIntent(true);
        } else {
            fileRef.current?.click();
        }
    };

    const confirmReplaceIntent = () => {
        setShowReplaceIntent(false);
        fileRef.current?.click();
    };

    const commitUpload = async (file) => {
        if (!file) return;
        const isReplacement = Boolean(script?.has_original || script?.filename);
        setUploading(true);
        setError("");
        setProgress("Uploading script…");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data: up } = await api.post(
                `${apiBase}/${projectId}/script/upload`,
                fd,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    // Iter-92: large PDFs upload over slow networks. Give the
                    // request a generous client-side budget that comfortably
                    // exceeds any sensible upload time but still fails fast
                    // if the request truly stalls. Backend defers heavy text
                    // extraction so the request itself returns quickly.
                    timeout: 120000,
                },
            );
            // Iter-143 — Trust the server response. The backend
            // (iter-142) returns the FINAL index_status synchronously;
            // we must use it verbatim and never fall back to the
            // prior local value, otherwise a stale `indexing` survives
            // a successful upload (the live deploy bug).
            const serverStatus = up?.index_status ?? "ready";
            setScript((prev) => ({
                ...(prev || {}),
                id: up?.id || prev?.id,
                filename: up?.filename || file.name,
                title: prev?.title || up?.filename || file.name,
                has_original: true,
                source: "upload",
                size: up?.size,
                chars: up?.chars,
                page_count: up?.page_count,
                index_status: serverStatus,
                index_error: up?.index_error ?? null,
                indexing_started_at: up?.indexing_started_at ?? null,
            }));
            setScriptTitle((t) => t || up?.filename || file.name);
            setSavedMsg(isReplacement ? "Replaced" : "Uploaded");
            setTimeout(() => setSavedMsg(""), 1500);
            // Iter-142 — fresh uploads return final state directly;
            // the `indexing` branch only fires for the legacy code path.
            if (serverStatus === "indexing") {
                setProgress("Reading the script…");
                toast.info("Script saved · still processing in background");
            } else if (serverStatus === "failed") {
                setProgress("");
                const indexErrText = typeof up?.index_error === "string"
                    ? up.index_error
                    : up?.index_error
                        ? JSON.stringify(up.index_error)
                        : "";
                const reason = indexErrText
                    ? `We couldn't finish reading the file: ${indexErrText}`
                    : "We couldn't finish reading the file. Please try uploading it again.";
                setError(reason);
                toast.error(reason);
            } else if (serverStatus === "empty") {
                setProgress("");
                toast.info("Script saved · no text found in the file");
            } else {
                setProgress("");
                setError("");
                toast.success(
                    isReplacement
                        ? `Script replaced · ${file.name}`
                        : `Script uploaded · ${file.name}`,
                );
            }
            setPendingFile(null);
            // Background canonical refresh — never throws to the user.
            api.get(`${apiBase}/${projectId}/script`)
                .then(({ data: sp }) => setScript(sp))
                .catch(() => {});
        } catch (err) {
            // Iter-145 — Production WiFi RCA. The upload POST can be
            // aborted by an intermediate proxy BEFORE the backend
            // finishes writing. A single follow-up GET fires too soon.
            // Poll for up to 30s waiting for the BE to settle. Only
            // declare failure if the slot is still empty after.
            setProgress("");
            setPendingFile(null);
            const MAX_ATTEMPTS = 60; // 60 × 1s = 60s. Iter-155B.4 — doubled the recovery window so genuinely-slow proxies never produce a false-failure toast.
            const POLL_MS = 1000;
            let recovered = false;
            let recoveredSp = null;
            // Calm signal during the recovery window so the user
            // knows we're still working rather than that the page
            // froze. Iter-157G P1 — wording aligned with the
            // revision-upload trust principle: never instruct the
            // user to retry while work is still running.
            const checkingToastId = toast.loading(
                `Script uploaded · reading ${file.name} — this can take a moment…`,
            );
            for (let attempt = 0; attempt < MAX_ATTEMPTS && !recovered; attempt++) {
                try {
                    const { data: sp } = await api.get(
                        `${apiBase}/${projectId}/script`,
                    );
                    // Iter-155B.2 — `has_original=true` means the
                    // file is saved server-side. Even if indexing is
                    // still running, the upload itself succeeded —
                    // so we MUST stop polling and show success +
                    // "still reading" rather than a false failure.
                    // This was the reported "upload succeeded but
                    // UI showed 'Something didn't work'" bug.
                    if (sp?.has_original) {
                        recovered = true;
                        recoveredSp = sp;
                        break;
                    }
                } catch {
                    /* swallow — keep polling */
                }
                if (!recovered) {
                    await new Promise((resolve) =>
                        setTimeout(resolve, POLL_MS),
                    );
                }
            }
            toast.dismiss(checkingToastId);
            if (recovered && recoveredSp) {
                setScript(recoveredSp);
                setScriptTitle(
                    (t) => t || recoveredSp.title || recoveredSp.filename || "",
                );
                setError("");
                if (recoveredSp.index_status === "ready") {
                    toast.success(
                        isReplacement
                            ? `Script replaced · ${file.name}`
                            : `Script uploaded · ${file.name}`,
                    );
                } else if (recoveredSp.index_status === "indexing") {
                    // Iter-155B.2 — explicit "still working" success path.
                    setProgress("Reading the script…");
                    toast.success(
                        `Script uploaded · still reading in the background`,
                    );
                } else if (recoveredSp.index_status === "empty") {
                    toast.info(`Script saved · no text found in the file`);
                } else if (recoveredSp.index_status === "failed") {
                    const reason = recoveredSp.index_error
                        ? `We couldn't finish reading the file: ${recoveredSp.index_error}`
                        : "We couldn't finish reading the file.";
                    setError(reason);
                    toast.error(reason);
                }
            } else {
                // Iter-155B.4 — never alarm the user on a recovery
                // exhaustion. The upload often DOES land server-side
                // after our 60s poll window; presenting a hard error
                // would only damage trust. We surface a calm,
                // forward-pointing info toast and instruct the user
                // where to check next.
                const calm =
                    "Upload is taking longer than usual. Check the Scripts panel in a moment — your file may have made it through.";
                setError("");
                toast.info(calm, { duration: 8000 });
            }
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const saveScriptText = async (e) => {
        e.preventDefault();
        if (!scriptText.trim()) return;
        try {
            await api.post(`${apiBase}/${projectId}/script/text`, {
                content: scriptText,
                title: scriptTitle.trim() || null,
            });
            const { data: sp } = await api.get(`${apiBase}/${projectId}/script`);
            setScript(sp);
            setSavedMsg("Saved");
            setTimeout(() => setSavedMsg(""), 1500);
        } catch (err) {
            setError(formatApiError(err));
        }
    };

    const _runParse = async () => {
        // Iter-157P.6 — Operation Pineapple workflow trust fix. The
        // producer's "Build Scenes & Cast" / "Build Again" button used
        // to call ONLY `/script/parse`, which creates DRAFT scenes
        // with brand-new UUIDs and brand-new `scene_characters`
        // linkages — but leaves the OLD CONFIRMED scenes in place
        // serving the operational Scene/Cast views. The producer
        // would press Build, see Scene 4 still showing KIWI, leave
        // and re-enter, then either explicitly Confirm on
        // ScenesReview or run Build a second time and find the
        // ScenesReview path — at which point the change finally took
        // effect. That two-step requirement was both confusing and
        // a trust hit ("did Build actually do anything?").
        //
        // We now do parse + confirm in one operation. After parse we
        // chain `/scenes/confirm` so the new drafts are promoted to
        // confirmed immediately, the OLD confirmed scenes are
        // preserved-and-replaced via the Iter-150 / Iter-157P.4
        // safety net, and the operational state matches the producer's
        // mental model: "Build = make this script real now." We then
        // broadcast `osf:scenes-changed` + `osf:scripts-changed` so
        // any open Scene / Cast page refetches without a manual
        // navigation.
        setParsing(true);
        setError("");
        setExtractionWarning(null);
        setProgress("Reading scenes and cast…");
        try {
            const { data } = await api.post(`${apiBase}/${projectId}/script/parse`);
            if (!data.parsed_count) {
                setError(
                    "No scene headings detected. Ensure the script uses standard INT./EXT. formatting, or add scenes manually.",
                );
                return;
            }
            // Promote the freshly-parsed drafts to confirmed so the
            // producer's Scene / Cast views immediately reflect the
            // new script. Wrapped in its own try/catch so a parse-
            // succeeded-but-confirm-failed flow still surfaces the
            // partial completion truthfully.
            let confirmData = null;
            setProgress("Updating scenes & cast…");
            try {
                const r = await api.post(`${apiBase}/${projectId}/scenes/confirm`);
                confirmData = r.data;
            } catch (confirmErr) {
                console.warn("auto-confirm failed", confirmErr);
                toast.message("Scenes drafted — confirmation needs your attention", {
                    description:
                        "Open Scenes Review to finalize the new scenes list. Your continuity is safe.",
                });
                return;
            }
            const parts = [`${data.parsed_count} scenes built`];
            if (data.skipped_count) parts.push(`${data.skipped_count} omitted`);
            if (data.new_characters_count)
                parts.push(`${data.new_characters_count} new cast`);
            if (data.attachments_count)
                parts.push(`${data.attachments_count} auto-attached`);
            toast.success(parts.join(" · "));
            // Iter-147 — apply the persistent build stamp locally.
            setScript((prev) => ({
                ...(prev || {}),
                parsed_at: data.parsed_at || new Date().toISOString(),
                parsed_scenes_count:
                    data.parsed_scenes_count ?? data.parsed_count ?? null,
                parsed_characters_count:
                    data.parsed_characters_count ?? data.characters_count ?? null,
            }));
            // Iter-157P.6 — Broadcast so open Scene / Cast / Schedule
            // views can refetch without the producer needing to
            // leave-and-return. Mirrors the `osf:scripts-changed`
            // pattern from Iter-157H.
            try {
                window.dispatchEvent(
                    new CustomEvent("osf:scenes-changed", {
                        detail: { projectId, source: "build_scenes_and_cast" },
                    }),
                );
                window.dispatchEvent(
                    new CustomEvent("osf:scripts-changed", {
                        detail: { projectId, source: "build_scenes_and_cast" },
                    }),
                );
            } catch (_) {
                /* no-op */
            }
            // Iter-149 (P2.5) — DO NOT auto-navigate. Stay where the
            // user was. Offer a Review action toast when items
            // landed in Needs Review.
            if ((data.possible_characters_count || 0) > 0) {
                toast.message("Some items need review", {
                    description: `${data.possible_characters_count} character${data.possible_characters_count === 1 ? "" : "s"} in Needs Review on the Cast page.`,
                    action: {
                        label: "Review",
                        onClick: () =>
                            navigate(
                                `/projects/${projectId}/cast`,
                                { state: { openNeedsReview: true } },
                            ),
                    },
                });
            }
            // Iter-150 — continuity preservation toast. Calm,
            // informational. Nothing was deleted; some items just
            // need manual reassignment.
            const needsRe =
                (confirmData?.continuity_needs_reassignment || 0) +
                (data.continuity_needs_reassignment || 0);
            if (needsRe > 0) {
                toast.message(
                    `${needsRe} continuity item${needsRe === 1 ? "" : "s"} need reassignment`,
                    {
                        description:
                            "Nothing was deleted — these items were detached during the rebuild and are kept safe.",
                    },
                );
            }
        } catch (err) {
            const status = err?.response?.status;
            if (status === 409) {
                setError("Script is still being read — please wait a few seconds and try Process again.");
            } else if (status === 422) {
                // Iter-157P.14 — structured base-script zero-scene
                // failure. The backend returns a producer-friendly
                // payload (code, message, active_format, filename)
                // and we render the same calm red block pattern
                // used in the revision extraction warning. Producer
                // gets a clear explanation + recovery action; no
                // silent half-build, no panic.
                const detail = err?.response?.data?.detail;
                if (detail && typeof detail === "object"
                    && detail.error === "script_yielded_zero_scenes") {
                    setExtractionWarning(detail);
                } else {
                    setError(
                        (detail && typeof detail === "string" && detail) ||
                            (detail && typeof detail === "object" && detail.message) ||
                            "We couldn't process the script. Please try again or upload a different file.",
                    );
                }
            } else {
                setError("We couldn't process the script. Please try again or upload a different file.");
            }
        } finally {
            setParsing(false);
            setProgress("");
            setShowRebuildConfirm(false);
        }
    };

    const parseNow = () => {
        // Iter-147 — gate re-parses behind a confirmation dialog so the
        // user explicitly accepts the scene-draft replacement.
        if (script?.parsed_at) {
            setShowRebuildConfirm(true);
            return;
        }
        return _runParse();
    };

    const indexStatus = script?.index_status;
    const indexing = indexStatus === "indexing";
    const indexFailed = indexStatus === "failed" || indexStatus === "empty";
    const scriptReady =
        Boolean(script?.content) && indexStatus !== "indexing";

    return (
        <section data-testid="script-section" className="space-y-4">
            {extractionWarning ? (() => {
                const w = extractionWarning;
                const active = w.active_format;
                // Iter-157P.19 — readability & persistence pass.
                // The previous copy explained the parser mechanics
                // ("we found text but couldn't identify scene
                // headings") which left producers without an
                // actionable next step. The new copy names the
                // most common cause (a "Print to PDF" style export
                // that bakes the script as an image / non-text
                // layer) and gives a concrete export setting to
                // try. Styling switches off text-red-100 (which
                // becomes near-white on light-mode's light-red
                // panel) onto the red-200/300 family which the
                // light-mode CSS overrides into a readable dark
                // red. Block is rendered straight into the panel
                // (no toast, no auto-dismiss) so the producer can
                // re-read it as many times as they need.
                const headline = active === "DOCX"
                    ? "We couldn\u2019t identify any scenes in this DOCX."
                    : active === "PDF"
                        ? "We couldn\u2019t identify any scenes in this PDF."
                        : "We couldn\u2019t identify any scenes in this script.";
                const explanation = active === "DOCX"
                    ? "This DOCX may have been exported in a format that doesn\u2019t preserve readable scene headings."
                    : "This PDF may have been exported in a format that doesn\u2019t preserve readable text.";
                const recommendation = active === "DOCX"
                    ? "Try re-exporting the DOCX from your screenwriting tool, or upload the PDF version of the script."
                    : active === "PDF"
                        ? "Try exporting the PDF using \u201CBest for electronic distribution and accessibility\u201D, or upload the DOCX version of the script."
                        : "Try exporting the PDF using \u201CBest for electronic distribution and accessibility\u201D, or upload the DOCX version of the script.";
                return (
                    <div
                        data-testid="script-extraction-warning"
                        role="alert"
                        aria-live="polite"
                        className="rounded-xl border border-red-500/25 bg-red-500/10 p-4 space-y-2"
                    >
                        <p className="text-sm text-red-300 font-semibold leading-snug">
                            {headline}
                        </p>
                        <p className="text-[13px] text-red-200 leading-relaxed">
                            {explanation}
                        </p>
                        <p className="text-[13px] text-red-200 leading-relaxed">
                            <span className="font-semibold">Try this:</span>{" "}
                            {recommendation}
                        </p>
                        <p className="text-[13px] text-emerald-300 leading-relaxed">
                            Your project is safe. No changes have been made.
                        </p>
                        <div className="pt-1">
                            <button
                                type="button"
                                data-testid="script-extraction-warning-dismiss"
                                onClick={() => setExtractionWarning(null)}
                                className="tap inline-flex items-center h-7 px-3 rounded-full text-[11px] uppercase tracking-[0.2em] text-red-200 border border-red-500/30 hover:bg-red-500/15"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                );
            })() : null}

            {error ? (
                <p data-testid="script-error" className="text-sm text-red-400 flex items-start gap-1.5">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                    <span>{error}</span>
                </p>
            ) : null}

            {progress ? (
                <p
                    data-testid="script-progress"
                    className="text-xs text-violet-200 bg-violet-500/10 border border-violet-500/20 rounded-md px-3 py-2 flex items-center gap-2"
                >
                    <Loader2 size={12} className="animate-spin" /> {progress}
                </p>
            ) : null}

            <div className="rounded-xl border border-white/10 bg-card p-4">
                <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                    Upload script file
                </Label>
                <p className="text-xs text-neutral-500 mt-1 mb-3">
                    PDF or DOCX (also TXT / Fountain). We&apos;ll extract text and keep the
                    original.
                </p>
                <input
                    ref={fileRef}
                    type="file"
                    data-testid="script-file-input"
                    onChange={uploadScript}
                    accept=".pdf,.docx,.txt,.fountain"
                    className="hidden"
                />
                <Button
                    data-testid="trigger-script-upload"
                    type="button"
                    disabled={uploading || indexing}
                    onClick={onUploadButtonClick}
                    className="w-full min-tap"
                    variant="secondary"
                >
                    <Upload size={16} className="mr-2 shrink-0" />
                    <span className="truncate">
                        {uploading
                            ? "Uploading…"
                            : indexing
                            ? "Reading script…"
                            : script?.filename
                            ? `Replace script`
                            : "Choose file"}
                    </span>
                </Button>
                {script?.filename ? (
                    <div
                        data-testid="script-uploaded-info"
                        className="mt-3 text-xs text-neutral-500 flex items-baseline gap-1.5 min-w-0"
                    >
                        <span className="shrink-0">Stored:</span>
                        <span
                            className="text-neutral-300 truncate min-w-0 flex-1"
                            title={script.filename}
                        >
                            {script.filename}
                        </span>
                        {indexStatus === "ready" ? (
                            <span
                                data-testid="script-index-status-ready"
                                className="shrink-0 text-[10px] uppercase tracking-[0.2em] text-emerald-300"
                            >
                                Ready
                            </span>
                        ) : indexing ? (
                            <span
                                data-testid="script-index-status-indexing"
                                className="shrink-0 text-[10px] uppercase tracking-[0.2em] text-violet-200 inline-flex items-center gap-1"
                            >
                                <Loader2 size={10} className="animate-spin" />{" "}
                                Reading
                            </span>
                        ) : indexFailed ? (
                            <span
                                data-testid="script-index-status-failed"
                                className="shrink-0 text-[10px] uppercase tracking-[0.2em] text-amber-300"
                            >
                                {indexStatus === "empty" ? "No text" : "Failed"}
                            </span>
                        ) : null}
                    </div>
                ) : null}
                {indexing ? (
                    <ScriptIndexingRecovery
                        startedAt={indexingStartedAt}
                        onRefresh={recoveryRefresh}
                        onRetry={recoveryRetry}
                        onReplace={recoveryReplace}
                        busy={recoveryBusy}
                        testidPrefix="script-recovery"
                    />
                ) : null}
                <ScriptParsedBadge
                    parsedAt={script?.parsed_at}
                    scenesCount={script?.parsed_scenes_count}
                    charactersCount={script?.parsed_characters_count}
                    testid="script-parsed-badge"
                />
            </div>

            <form
                onSubmit={saveScriptText}
                className="rounded-xl border border-white/10 bg-card p-4 space-y-3"
            >
                <Label className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                    Or paste script text
                </Label>
                <Input
                    data-testid="script-title-input"
                    placeholder="Title (optional)"
                    value={scriptTitle}
                    onChange={(e) => setScriptTitle(e.target.value)}
                    className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary"
                />
                <Textarea
                    data-testid="script-text-input"
                    placeholder="Paste your script here…"
                    value={scriptText}
                    onChange={(e) => setScriptText(e.target.value)}
                    rows={10}
                    className="bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary resize-y mono text-xs"
                />
                <Button
                    data-testid="save-script-text"
                    type="submit"
                    className="w-full min-tap"
                    variant="secondary"
                    disabled={!scriptText.trim()}
                >
                    <FileText size={16} className="mr-2" /> Save pasted text
                </Button>
                {savedMsg ? (
                    <p data-testid="script-saved" className="text-xs text-emerald-400 text-center">
                        {savedMsg}
                    </p>
                ) : null}
            </form>

            {/* Iter-155B.1 — Layperson guidance: once a script is
                uploaded but Step 2 hasn't been pressed yet, draw the
                eye to the Build button with a calm contextual hint
                that explains what's about to happen and what stays
                untouched. No multi-step wizard, no banner — just a
                soft prompt next to the action. Once parsed_at is set
                we drop the prompt entirely (job done). */}
            {scriptReady && !script?.parsed_at && !parsing && !indexing ? (
                <div
                    data-testid="build-next-prompt"
                    className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5 text-[12px] text-primary/90 leading-relaxed"
                >
                    <span className="font-semibold text-primary">Next:</span>{" "}
                    Build the scenes & cast list from this script.
                    Tap below — your continuity (photos, notes, looks)
                    is never touched by this step.
                </div>
            ) : null}

            <Button
                data-testid="parse-script-btn"
                type="button"
                onClick={parseNow}
                disabled={!scriptReady || parsing || indexing}
                className={`w-full min-tap font-semibold tracking-wide ${
                    scriptReady && !script?.parsed_at && !parsing && !indexing
                        ? "ring-2 ring-primary/40 ring-offset-2 ring-offset-background"
                        : ""
                }`}
            >
                <Wand2 size={16} className="mr-2" />
                {parsing
                    ? "Building scenes & cast…"
                    : indexing
                    ? "Still reading the file… please wait"
                    : script?.parsed_at
                    ? "Build again"
                    : "Build Scenes & Cast"}
            </Button>
            {!scriptReady && !indexing ? (
                <p className="text-xs text-neutral-500 text-center">
                    Upload or paste a script first.
                </p>
            ) : null}

            {/* Iter-158.13.2.20b — Route 2 parity restored. The
                mature ScriptRevisionPanel now works on Route 2 via
                its existing `apiBase`/`routeBase` handling; the
                Iter-158.6.A1 Phase A beta stub is retired. */}
            {scriptReady && script?.id ? (
                <ScriptRevisionPanel
                    projectId={projectId}
                    scriptId={script.id}
                />
            ) : null}

            <ConfirmReplaceScriptDialog
                open={showReplaceIntent || Boolean(pendingFile)}
                onOpenChange={(v) => {
                    if (!v) {
                        setShowReplaceIntent(false);
                        setPendingFile(null);
                    }
                }}
                currentFilename={script?.filename}
                onConfirm={() => {
                    if (pendingFile) {
                        commitUpload(pendingFile);
                    } else {
                        confirmReplaceIntent();
                    }
                }}
                busy={uploading}
            />

            <ConfirmRebuildSceneCastDialog
                open={showRebuildConfirm}
                onOpenChange={setShowRebuildConfirm}
                scenesCount={script?.parsed_scenes_count}
                charactersCount={script?.parsed_characters_count}
                onConfirm={_runParse}
                busy={parsing}
            />
        </section>
    );
}
